/**
 * Hub centralizado do lead no kanban — junção de /clientes, /agendamentos, /contratos e /contratos/:id
 * Layout estilo Kommo: painel esquerdo (abas) + timeline (direita)
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, message, Tabs, Spin, Skeleton, Badge, Alert, Popover, Tooltip } from 'antd';
import { X, Clock, Calendar, FileText, User, Paperclip, AlertCircle, FileCheck, Sparkles, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import {
  Popover as DatePopover,
  PopoverContent as DatePopoverContent,
  PopoverTrigger as DatePopoverTrigger,
} from '@/components/ui/popover';
import { AgendamentoForm } from '@/features/agendamentos/components/AgendamentoForm';
import { Agendamento } from '@/features/agendamentos/types/agendamento';
import { agendamentosService } from '@/features/agendamentos/services/agendamentos.service';
import { ClienteInfoDisplay } from '@/features/clientes/components/ClienteInfoDisplay';
import { ClienteFormModal } from '@/features/clientes/components/ClienteFormModal';
import { ClientesService } from '@/features/clientes/services/clientes.service';
import { TimelineContent } from '@/features/vendas/components/TimelineContent';
import { ClienteComentariosSection } from '@/features/vendas/components/ClienteComentariosSection';
import { TimelineEvent } from '@/features/vendas/hooks/useClientJourney';
import { useAuth } from '@/features/auth';
import { useCreateCard, useUpdateCard } from '../hooks/usePipelineCard';
import { usePipelineTags } from '../hooks/usePipelineTags';
import { usePipelinesWithStages } from '../hooks/usePipelinesWithStages';
import { useMoveCardToPipelineStage } from '../hooks/useMoveCardToPipelineStage';
import { PipelineStageMoverPopover } from './PipelineStageMoverPopover';
import { PipelineLeadContratosTab } from './PipelineLeadContratosTab';
import { PipelineLeadAnexosTab } from './PipelineLeadAnexosTab';
import { PipelineLeadActionsMenu } from './PipelineLeadActionsMenu';
import { PipelineLeadTagsModal } from './PipelineLeadTagsModal';
import { PipelineLeadPrintView } from './PipelineLeadPrintView';
import type { PipelineCard } from '../types';

type LeadTab = 'cliente' | 'agendamento' | 'contratos' | 'anexos';

/** Referência estável para evitar loop de re-render quando não há tags */
export const EMPTY_LEAD_TAGS: string[] = [];

/** Combina a data selecionada com a hora HH:mm num único Date */
function composeDataCriacao(date: Date, hora: string): Date {
  const [h, m] = hora.split(':').map(Number);
  const composed = new Date(date);
  composed.setHours(h || 0, m || 0, 0, 0);
  return composed;
}

interface PipelineLeadModalProps {
  open: boolean;
  pipelineId: string;
  stageId: string;
  stageName?: string;
  clienteId?: string;
  agendamentoId?: number;
  leadName?: string;
  cardId?: string;
  dadosCard?: PipelineCard['dados_card'];
  initialTags?: string[];
  onClose: () => void;
  onLeadDeleted?: () => void;
  card?: PipelineCard;
}

export function PipelineLeadModal({
  open,
  pipelineId,
  stageId,
  stageName,
  clienteId: initialClienteId,
  agendamentoId,
  leadName,
  cardId,
  dadosCard,
  initialTags = EMPTY_LEAD_TAGS,
  onClose,
  onLeadDeleted,
  card,
}: PipelineLeadModalProps) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const createCard = useCreateCard();
  const updateCard = useUpdateCard();
  const { tagsById } = usePipelineTags();
  const { pipelinesWithStages, loading: loadingPipelinesWithStages } = usePipelinesWithStages();
  const { moveCard } = useMoveCardToPipelineStage();

  const [saving, setSaving] = useState(false);
  /** Alterações no formulário de agendamento ainda não enviadas ao banco */
  const [agendamentoDirty, setAgendamentoDirty] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [pipelineMoverOpen, setPipelineMoverOpen] = useState(false);
  const [movingStageId, setMovingStageId] = useState<string | null>(null);
  const [clienteModalOpen, setClienteModalOpen] = useState(false);
  const [tags, setTags] = useState<string[]>(initialTags);
  const [activeTab, setActiveTab] = useState<LeadTab>(
    initialClienteId ? 'agendamento' : 'agendamento'
  );
  const [resolvedClienteId, setResolvedClienteId] = useState<string | undefined>(
    initialClienteId
  );
  const [activeAgendamentoId, setActiveAgendamentoId] = useState<number | undefined>(
    agendamentoId
  );

  const [dealName, setDealName] = useState('');
  const [isEditingDealName, setIsEditingDealName] = useState(false);
  const [editingText, setEditingText] = useState('');

  // Data/hora de criação do lead — editável; enviada como data_criacao (created_at fica automático)
  const [dataCriacao, setDataCriacao] = useState<Date>(() => new Date());
  const [horaCriacao, setHoraCriacao] = useState<string>(() => format(new Date(), 'HH:mm'));

  useEffect(() => {
    if (!open) return;
    setResolvedClienteId(initialClienteId);
    setActiveAgendamentoId(agendamentoId);
    setActiveTab('agendamento');
    setTags(initialTags);
    setAgendamentoDirty(false);

    const baseDataCriacao = card?.data_criacao ? new Date(card.data_criacao) : new Date();
    setDataCriacao(baseDataCriacao);
    setHoraCriacao(format(baseDataCriacao, 'HH:mm'));

    const contrato = card?.contratos;
    const grupoTitulo = contrato?.grupos?.titulo || (contrato?.grupos?.numero_grupo ? `Grupo ${contrato.grupos.numero_grupo}` : '') || dadosCard?.grupo_titulo;
    const bemTipo = contrato?.bens?.tipo_bem || dadosCard?.bem_tipo;
    const resolvedName = (grupoTitulo && bemTipo) 
      ? `${grupoTitulo} - ${bemTipo}` 
      : (dadosCard?.nome_negocio as string || dadosCard?.nome as string || 'Sem Nome');
    setDealName(resolvedName);
    setEditingText(resolvedName);
    setIsEditingDealName(false);
  }, [open, initialClienteId, agendamentoId, initialTags, card, dadosCard]);

  const { data: agendamento, isLoading: loadingAgendamento } = useQuery({
    queryKey: ['agendamento', activeAgendamentoId],
    queryFn: () => agendamentosService.buscarAgendamento(activeAgendamentoId!),
    enabled: open && !!activeAgendamentoId,
  });

  const { data: cliente, isLoading: loadingCliente } = useQuery({
    queryKey: ['cliente', resolvedClienteId],
    queryFn: () => ClientesService.buscarCliente(resolvedClienteId!),
    enabled: open && !!resolvedClienteId,
    staleTime: 0,
  });

  const { data: documentos = [] } = useQuery({
    queryKey: ['documentos', resolvedClienteId],
    queryFn: () => ClientesService.listarDocumentos(resolvedClienteId!),
    enabled: open && !!resolvedClienteId,
    staleTime: 0,
  });

  const responsavelNome =
    [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || 'Você';

  const displayName =
    leadName || cliente?.nome || agendamento?.cliente?.nome || 'Novo Lead';

  const currentPipelineName = pipelinesWithStages.find(
    (p) => p.pipeline.id === pipelineId
  )?.pipeline.nome;

  const handleSelectStage = (destPipelineId: string, destStageId: string) => {
    if (!card) return;
    setMovingStageId(destStageId);
    moveCard.mutate(
      { card, destPipelineId, destStageId },
      {
        onSuccess: ({ crossPipeline }) => {
          message.success(crossPipeline ? 'Card movido para outra pipeline' : 'Etapa atualizada');
          setPipelineMoverOpen(false);
          setMovingStageId(null);
          if (crossPipeline) onClose();
        },
        onError: (err) => {
          message.error(err instanceof Error ? err.message : 'Erro ao mover card');
          setMovingStageId(null);
        },
      }
    );
  };

  const tagNames = useMemo(
    () => tags.map((id) => tagsById.get(id)?.nome).filter((n): n is string => !!n),
    [tags, tagsById]
  );

  const exportRows = useMemo(
    () => [
      { campo: 'Lead', valor: displayName },
      { campo: 'Etapa', valor: stageName || '—' },
      { campo: 'Responsável', valor: responsavelNome },
      { campo: 'Cliente ID', valor: resolvedClienteId || '—' },
      { campo: 'Agendamento ID', valor: activeAgendamentoId ? String(activeAgendamentoId) : '—' },
      { campo: 'Telefone', valor: String(agendamento?.cliente?.tel1 || cliente?.tel1 || '—') },
      { campo: 'Email', valor: String(cliente?.email || agendamento?.cliente?.email || '—') },
      { campo: 'Tags', valor: tagNames.length ? tagNames.map((t) => `#${t}`).join(', ') : '—' },
    ],
    [
      displayName,
      stageName,
      responsavelNome,
      resolvedClienteId,
      activeAgendamentoId,
      agendamento,
      cliente,
      tagNames,
    ]
  );

  const exportFileName = `lead-${cardId || resolvedClienteId || 'novo'}`.replace(/[^\w-]+/g, '-');

  const handleSaveTags = async (nextTags: string[]) => {
    if (!cardId) {
      setTags(nextTags);
      return;
    }
    await updateCard.mutateAsync({
      cardId,
      dadosCard: {
        ...(dadosCard || { nome: displayName }),
        tags: nextTags,
      },
    });
    setTags(nextTags);
  };

  const handleSaveDealName = async () => {
    const cleanText = editingText.trim();
    if (!cleanText) {
      setEditingText(dealName);
      setIsEditingDealName(false);
      return;
    }
    if (cleanText === dealName) {
      setIsEditingDealName(false);
      return;
    }
    
    if (!cardId) {
      setDealName(cleanText);
      setIsEditingDealName(false);
      return;
    }
    
    try {
      const novosDados = {
        ...(dadosCard || {}),
        nome_negocio: cleanText,
      };

      await updateCard.mutateAsync({
        cardId,
        dadosCard: novosDados,
      });
      
      setDealName(cleanText);
      setIsEditingDealName(false);
      message.success('Nome do negócio atualizado!');
    } catch (err: any) {
      message.error(`Erro ao salvar: ${err.message}`);
    }
  };

  const persistDataCriacao = async (date: Date, hora: string) => {
    if (!cardId) return;
    try {
      await updateCard.mutateAsync({
        cardId,
        dataCriacao: composeDataCriacao(date, hora).toISOString(),
      });
      message.success('Data de criação atualizada');
    } catch (err) {
      message.error(
        err instanceof Error ? `Erro ao atualizar data: ${err.message}` : 'Erro ao atualizar data'
      );
    }
  };

  /** Fecha o modal pedindo confirmação se houver alterações não salvas */
  const handleRequestClose = () => {
    if (!agendamentoDirty) {
      onClose();
      return;
    }
    Modal.confirm({
      title: 'Descartar alterações?',
      content: 'Existem alterações no agendamento que ainda não foram salvas.',
      okText: 'Descartar',
      okButtonProps: { danger: true },
      cancelText: 'Continuar editando',
      zIndex: 1100,
      centered: true,
      className: 'pipeline-lead-discard-confirm',
      onOk: onClose,
    });
  };

  const handleLeadDeleted = () => {
    onClose();
    onLeadDeleted?.();
  };

  const handleCreated = useCallback(async (novoAgendamento: Agendamento) => {
    if (!profile?.uuid || profile.tenant_id == null) {
      throw new Error('Usuário não autenticado');
    }

    const clienteAg = novoAgendamento.cliente;

    if (novoAgendamento.cliente_id) {
      setResolvedClienteId(String(novoAgendamento.cliente_id));
      setActiveAgendamentoId(novoAgendamento.id);
      void queryClient.invalidateQueries({
        queryKey: ['client-journey', String(novoAgendamento.cliente_id)],
      });
    }

    if (initialClienteId) {
      queryClient.setQueryData(['agendamento', novoAgendamento.id], novoAgendamento);
      // Mantém o card do kanban em sincronia com o agendamento editado
      if (cardId) {
        await updateCard.mutateAsync({
          cardId,
          dataParaAgendamento: novoAgendamento.data_agendamento?.slice(0, 10),
          dadosCardPatch: {
            agendamento_id: novoAgendamento.id,
            hora_agendamento: novoAgendamento.hora_agendamento?.slice(0, 5),
            status_agendamento: novoAgendamento.status,
          },
        });
      }
    } else {
      const dataCriacaoCompleta = composeDataCriacao(dataCriacao, horaCriacao);

      await createCard.mutateAsync({
        pipelineId,
        stageId,
        tenantId: Number(profile.tenant_id),
        responsavelId: profile.uuid,
        clienteId: novoAgendamento.cliente_id,
        dataParaAgendamento: novoAgendamento.data_agendamento,
        horaAgendamento: novoAgendamento.hora_agendamento,
        dataCriacao: dataCriacaoCompleta.toISOString(),
        dadosCard: {
          nome: clienteAg?.nome || 'Sem nome',
          nome_negocio: dealName !== 'Sem Nome' && dealName !== 'Novo Lead' ? dealName : undefined,
          telefone: clienteAg?.tel1 || clienteAg?.tel2,
          interesse: novoAgendamento.observacoes,
          agendamento_id: novoAgendamento.id,
          hora_agendamento: novoAgendamento.hora_agendamento?.slice(0, 5),
        },
      });
    }
  }, [profile, initialClienteId, cardId, updateCard, createCard, pipelineId, stageId, queryClient, dealName, dataCriacao, horaCriacao]);

  const handleTimelineEvent = useCallback((event: TimelineEvent) => {
    if (event.type === 'AGENDAMENTO' && event.data?.id) {
      setActiveAgendamentoId(event.data.id);
      setActiveTab('agendamento');
    } else if (event.type === 'CONTRATO' && event.data?.id) {
      setActiveTab('contratos');
    } else if (event.type === 'CONVERSAO') {
      setActiveTab('cliente');
    }
  }, []);

  const showCreateFooter = activeTab === 'agendamento' && !initialClienteId;
  const showSaveFooter = activeTab === 'agendamento' && !!initialClienteId && agendamentoDirty;

  const tabItems = useMemo(
    () => [
      {
        key: 'agendamento',
        label: (
          <span className="flex items-center gap-1.5 text-xs">
            <Calendar className="w-3.5 h-3.5" /> Agendamento
          </span>
        ),
        children: (
          <div className="pipeline-lead-tab-panel px-3 py-3">
            {loadingAgendamento && activeAgendamentoId ? (
              <div className="flex justify-center py-10">
                <Spin />
              </div>
            ) : (
              <AgendamentoForm
                agendamento={agendamento ?? undefined}
                variant="pipeline"
                hideActions
                embedded
                hideClienteField={!!resolvedClienteId}
                responsavelNome={responsavelNome}
                onLoadingChange={setSaving}
                onDirtyChange={setAgendamentoDirty}
                onCreated={handleCreated}
                preselectedClienteId={
                  resolvedClienteId ? parseInt(resolvedClienteId, 10) : undefined
                }
                onSuccess={() => {
                  // Atualização já exibe toast no próprio formulário
                  if (!initialClienteId) {
                    message.success('Lead adicionado à pipeline');
                    onClose();
                  } else if (!agendamento) {
                    message.success('Agendamento salvo');
                  }
                }}
              />
            )}
          </div>
        ),
      },
      {
        key: 'cliente',
        label: (
          <span className="flex items-center gap-1.5 text-xs">
            <User className="w-3.5 h-3.5" /> Cliente
          </span>
        ),
        children: (
          <div className="pipeline-lead-tab-panel px-3 py-3">
            {!resolvedClienteId ? (
              <div className="text-center py-10 px-4">
                <User className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Salve um agendamento para vincular o cliente
                </p>
                <button
                  type="button"
                  onClick={() => setActiveTab('agendamento')}
                  className="mt-3 text-xs text-[#FF3737] hover:underline"
                >
                  Ir para Agendamento
                </button>
              </div>
            ) : loadingCliente ? (
              <div className="p-4 space-y-3">
                <Skeleton active paragraph={{ rows: 6 }} />
              </div>
            ) : cliente ? (
              <ClienteInfoDisplay
                cliente={cliente}
                onEdit={() => setClienteModalOpen(true)}
              />
            ) : null}
          </div>
        ),
      },
      {
        key: 'contratos',
        disabled: !resolvedClienteId,
        label: (
          <span className="flex items-center gap-1.5 text-xs">
            <FileText className="w-3.5 h-3.5" /> Contratos
          </span>
        ),
        children: (
          <div className="pipeline-lead-tab-panel">
            {resolvedClienteId ? (
              <PipelineLeadContratosTab clienteId={resolvedClienteId} />
            ) : (
              <div className="text-center py-10 text-xs text-gray-400 px-4">
                Vincule um cliente para ver contratos
              </div>
            )}
          </div>
        ),
      },
      {
        key: 'anexos',
        disabled: !resolvedClienteId,
        label: (
          <span className="flex items-center gap-1.5 text-xs">
            <Paperclip className="w-3.5 h-3.5" /> Anexos
            {resolvedClienteId && (
              documentos.length > 0 ? (
                <Badge count={documentos.length} style={{ backgroundColor: '#FF3737' }} />
              ) : (
                <AlertCircle className="w-3.5 h-3.5 text-orange-500" />
              )
            )}
          </span>
        ),
        children: (
          <div className="pipeline-lead-tab-panel">
            {resolvedClienteId ? (
              <PipelineLeadAnexosTab
                clienteId={resolvedClienteId}
                onGoToCliente={() => setActiveTab('cliente')}
              />
            ) : (
              <div className="text-center py-10 text-xs text-gray-400 px-4">
                Vincule um cliente para ver anexos
              </div>
            )}
          </div>
        ),
      },
    ],
    [
      resolvedClienteId,
      loadingCliente,
      cliente,
      loadingAgendamento,
      activeAgendamentoId,
      agendamento,
      responsavelNome,
      handleCreated,
      initialClienteId,
      onClose,
    ]
  );

  return (
    <>
      <PipelineLeadPrintView
        enabled={open}
        displayName={displayName}
        stageName={stageName}
        responsavelNome={responsavelNome}
        tags={tagNames}
        clienteId={resolvedClienteId}
        cliente={cliente ?? null}
        agendamento={agendamento ?? null}
      />

      <Modal
      open={open}
      onCancel={handleRequestClose}
      footer={null}
      closable={false}
      width="min(1400px, 98vw)"
      destroyOnHidden
      centered
      className="pipeline-lead-modal"
      styles={{
        mask: { background: 'rgba(0, 0, 0, 0.45)' },
        body: { padding: 0, overflow: 'hidden' },
      }}
      wrapClassName="pipeline-lead-modal-wrapper"
      zIndex={1000}
    >
      <PipelineLeadTagsModal
        open={tagsOpen}
        onClose={() => setTagsOpen(false)}
        tags={tags}
        onSave={handleSaveTags}
        saving={updateCard.isPending}
      />

      <ClienteFormModal
        open={clienteModalOpen}
        cliente={cliente}
        onClose={() => setClienteModalOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['cliente', resolvedClienteId] });
          setClienteModalOpen(false);
        }}
      />

      <button
        type="button"
        onClick={handleRequestClose}
        className="pipeline-lead-print-hide absolute top-4 right-4 z-50 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-500 dark:text-gray-400 transition"
        aria-label="Fechar"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="pipeline-lead-screen-only flex h-[min(780px,90vh)] bg-white dark:bg-zinc-900">
        {/* Painel esquerdo — abas */}
        <div className="w-[min(520px,48%)] flex-shrink-0 flex flex-col border-r border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 min-h-0">
          <div className="bg-gradient-to-r from-[#0d0d0f] to-[#1c1c1f] text-white px-5 pt-5 pb-4 flex-shrink-0 space-y-3 border-b border-white/10">
            <div className="flex items-start justify-between gap-3 pr-8">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-1">
                  {resolvedClienteId ? (
                    <>
                      <FileCheck className="w-3.5 h-3.5 text-white/60" />
                      <p className="text-[10px] text-white/50 uppercase tracking-widest font-semibold">
                        Negócio
                      </p>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-3.5 h-3.5 text-white/60" />
                      <p className="text-[10px] text-white/50 uppercase tracking-widest font-semibold">
                        Novo lead
                      </p>
                    </>
                  )}
                  <div className="flex items-center gap-1 ml-1.5">
                    <DatePopover>
                      <DatePopoverTrigger asChild>
                        <button
                          type="button"
                          title="Data de criação do lead"
                          className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/10 border border-white/15 hover:bg-white/20 text-[11px] font-medium text-white/80 hover:text-white transition-colors"
                        >
                          <Calendar className="w-3 h-3" />
                          {format(dataCriacao, 'dd/MM/yyyy')}
                        </button>
                      </DatePopoverTrigger>
                      <DatePopoverContent className="w-auto p-0 z-[1100]" align="start">
                        <CalendarPicker
                          mode="single"
                          selected={dataCriacao}
                          onSelect={(date) => {
                            if (!date) return;
                            setDataCriacao(date);
                            void persistDataCriacao(date, horaCriacao);
                          }}
                          locale={ptBR}
                          initialFocus
                        />
                      </DatePopoverContent>
                    </DatePopover>
                    <input
                      type="time"
                      value={horaCriacao}
                      onChange={(e) => setHoraCriacao(e.target.value)}
                      onBlur={() => void persistDataCriacao(dataCriacao, horaCriacao)}
                      title="Hora de criação do lead"
                      className="px-1.5 py-0.5 rounded bg-white/10 border border-white/15 hover:bg-white/20 text-[11px] font-medium text-white/80 hover:text-white focus:outline-none focus:border-white/40 transition-colors [color-scheme:dark]"
                    />
                  </div>
                </div>
                {isEditingDealName ? (
                  <input
                    type="text"
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    onBlur={handleSaveDealName}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveDealName();
                      } else if (e.key === 'Escape') {
                        setEditingText(dealName);
                        setIsEditingDealName(false);
                      }
                    }}
                    className="bg-white/10 text-white text-[15px] font-semibold leading-tight px-2 py-1 rounded border border-white/20 focus:outline-none focus:border-white/50 w-full"
                    autoFocus
                  />
                ) : (
                  <h2 
                    onClick={() => setIsEditingDealName(true)}
                    className="text-[15px] font-semibold leading-tight truncate cursor-pointer hover:bg-white/10 px-2 py-1 -mx-2 rounded transition flex items-center gap-1.5 group select-none"
                    title="Clique para editar o nome do negócio"
                  >
                    <span>{dealName}</span>
                    <span className="text-white/30 group-hover:text-white/60 text-[10px] font-normal transition-colors flex-shrink-0">
                      (clique para editar)
                    </span>
                  </h2>
                )}
                {resolvedClienteId && (
                  <p className="text-[11px] text-white/60 mb-0 mt-1 truncate">
                    Cliente: <span className="font-medium">{displayName}</span>
                  </p>
                )}
              </div>
              <PipelineLeadActionsMenu
                cardId={cardId}
                fileName={exportFileName}
                exportRows={exportRows}
                onManageTags={() => setTagsOpen(true)}
                onDeleted={handleLeadDeleted}
              />
            </div>

            <Popover
              trigger="click"
              placement="bottomLeft"
              open={pipelineMoverOpen}
              onOpenChange={(next) => card && setPipelineMoverOpen(next)}
              content={
                <PipelineStageMoverPopover
                  pipelinesWithStages={pipelinesWithStages}
                  loading={loadingPipelinesWithStages}
                  currentPipelineId={pipelineId}
                  currentStageId={stageId}
                  movingStageId={movingStageId}
                  onSelectStage={handleSelectStage}
                />
              }
            >
              <Tooltip title={card ? undefined : 'Salve o lead antes de mover'}>
                <button
                  type="button"
                  disabled={!card}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 disabled:hover:bg-white/5 disabled:cursor-not-allowed text-sm backdrop-blur-sm transition-colors"
                >
                  <span className="truncate text-[13px] font-semibold flex items-center gap-1.5 min-w-0">
                    {currentPipelineName && (
                      <span className="text-white/50 font-normal flex-shrink-0">
                        {currentPipelineName} ›
                      </span>
                    )}
                    <span className="truncate">{stageName || 'Nova etapa'}</span>
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-white/50 flex-shrink-0" />
                </button>
              </Tooltip>
            </Popover>

            <button
              type="button"
              onClick={() => setTagsOpen(true)}
              className="w-full text-[11px] uppercase tracking-wider text-white/60 hover:text-white border border-white/20 hover:border-white/40 rounded-lg px-3 py-2 transition-all font-semibold bg-white/5 hover:bg-white/10 backdrop-blur-sm flex items-center justify-center gap-1.5"
            >
              {tags.length > 0 ? (
                <span className="flex items-center gap-1.5 justify-center flex-wrap">
                  {tags.map((tagId) => {
                    const tag = tagsById.get(tagId);
                    return (
                      <span
                        key={tagId}
                        className="inline-block px-2 py-0.5 rounded text-[10px]"
                        style={{
                          background: tag?.cor ? `${tag.cor}4D` : 'rgba(59,130,246,0.3)',
                          color: tag?.cor || '#bfdbfe',
                        }}
                      >
                        {tag?.nome ?? '…'}
                      </span>
                    );
                  })}
                </span>
              ) : (
                <>
                  <Paperclip className="w-3.5 h-3.5" />
                  <span>Adicionar tags</span>
                </>
              )}
            </button>
          </div>

          <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <Tabs
              activeKey={activeTab}
              onChange={(key) => setActiveTab(key as LeadTab)}
              items={tabItems}
              className="pipeline-lead-tabs"
              style={{ marginBottom: 0 }}
              tabBarStyle={{
                margin: 0,
                padding: '0 0.75rem',
                flexShrink: 0,
              }}
            />

            {showCreateFooter && (
              <div className="relative z-10 border-t border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex-shrink-0 p-4">
                <button
                  type="submit"
                  form="pipeline-lead-form"
                  disabled={saving}
                  className="w-full px-4 py-3 rounded-lg bg-[#FF3737] hover:bg-[#D92B2B] disabled:opacity-60 text-white text-sm font-semibold transition-colors"
                >
                  {saving ? 'Salvando...' : 'Criar lead'}
                </button>
              </div>
            )}

            {showSaveFooter && (
              <div className="relative z-10 border-t border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex-shrink-0 p-4">
                <button
                  type="submit"
                  form="pipeline-lead-form"
                  disabled={saving}
                  className="w-full px-4 py-3 rounded-lg bg-[#FF3737] hover:bg-[#D92B2B] disabled:opacity-60 text-white text-sm font-semibold transition-colors"
                >
                  {saving ? 'Salvando...' : 'Salvar alterações'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Painel direito — timeline */}
        <div className="flex-1 flex flex-col min-w-0 bg-gray-50 dark:bg-zinc-950 min-h-0">
          <div className="px-5 py-3 border-b border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex-shrink-0">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Timeline de Evolução
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Clique em um evento para abrir na aba correspondente
            </p>
          </div>

          <div className="flex-1 overflow-y-auto p-4 min-h-0">
            {!resolvedClienteId ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center max-w-xs">
                  <Clock className="w-10 h-10 text-gray-300 dark:text-zinc-600 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Timeline indisponível
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Crie ou vincule um agendamento para acompanhar a jornada do cliente
                  </p>
                </div>
              </div>
            ) : (
              <TimelineContent
                clienteId={resolvedClienteId}
                cardId={cardId}
                variant="embedded"
                highlightAgendamentoId={activeAgendamentoId}
                onEventClick={handleTimelineEvent}
                onGoToContratos={() => setActiveTab('contratos')}
              />
            )}
          </div>

          <ClienteComentariosSection
            clienteId={resolvedClienteId}
            cardId={cardId}
            enabled={open && !!resolvedClienteId}
          />
        </div>
      </div>
    </Modal>
    </>
  );
}

export default PipelineLeadModal;
